/* biome-ignore assist/source/organizeImports: Import order for Providers needs to match their order in the JSX hierarchy when they depend on each other. 
 For example: Toast/notification styles will break here if it is imported BEFORE the parent GlobalThemeProvider it depends on. */
import "@expert/theme/global.css";
import "@mantine/notifications/styles.css";

import { ContentfulClientProvider } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { initAppcues, initFullStory } from "@expert/monitoring";
import { GlobalThemeProvider } from "@expert/theme";
import { Notifications } from "@mantine/notifications";
import { RootAnalyticsProvider } from "@soluto-private/eventualize-react";
import { FeedbackContextProvider } from "@soluto-private/expert-workspace-feedback";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createClient } from "contentful";
import { type JSX, useEffect } from "react";
import { RouterProvider } from "react-router";
import { env } from "@/config";
import { transformDispatcher } from "./analytics";
import { router } from "./router";
import { enhanceAnalytics } from "./utils";

const IS_DEV = import.meta.env.VITE_ENV_TYPE === "development";
const ENABLE_TANSTACK_REACT_QUERY_DEVTOOLS = import.meta.env.VITE_ENABLE_TANSTACK_REACT_QUERY_DEVTOOLS === "true";

const logger = getLogger({ module: "App" });
window.addEventListener("unhandledrejection", ({ reason }: { reason: unknown }) => {
    logger.error({ err: reason }, "unhandledrejection");
});

const queryClient = new QueryClient({ defaultOptions: { queries: { gcTime: 1000 * 60 * 60 /* 1 hour */ } } });

const contentfulClient = createClient(env.contentful);

export default function App(): JSX.Element {
    useEffect(() => {
        transformDispatcher((dispatcher) => {
            return dispatcher.withEnhancer(enhanceAnalytics);
        });
        initFullStory({
            orgId: env.fullstoryOrgId,
            isDevMode: !env.fullstoryEnabled,
        });
        initAppcues({
            isEnabled: env.appcuesEnabled,
            url: env.appcuesUrl,
            appName: env.appName,
            channel: env.channel,
        });
    }, []);

    return (
        <GlobalThemeProvider>
            <Notifications zIndex={1000} containerWidth={320} />
            <RootAnalyticsProvider>
                <ContentfulClientProvider client={contentfulClient}>
                    <QueryClientProvider client={queryClient}>
                        <FeedbackContextProvider expertFeedbackUrl={env.expertFeedbackUrl}>
                            <RouterProvider router={router} />
                        </FeedbackContextProvider>
                        {IS_DEV && ENABLE_TANSTACK_REACT_QUERY_DEVTOOLS && <ReactQueryDevtools client={queryClient} />}
                    </QueryClientProvider>
                </ContentfulClientProvider>
            </RootAnalyticsProvider>
        </GlobalThemeProvider>
    );
}
